"use client";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  useI18N,
  usePostSaveVenue,
  useSavedPlaces,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import {
  savedPlaceIdToViewAtom,
  savedPlaceIdToViewDataChangedAtom,
} from "@/state/atoms";
import { useEffect, useRef, useState } from "react";
import { bucketUrlPrefix } from "common/config";
import AnalyticsService from "@/services/analytics";
import { useAtom } from "jotai";
import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";
import { CheckCircle, PlusCircle } from "phosphor-react";

interface AddRemoveVenueToListProps {
  onClickCreateNew: () => void;
  onClose: () => void;
  businessId: number;
  venueDetails: any;
  listNameCreated: string;
}

const AddRemoveVenueToList = ({
  onClickCreateNew,
  onClose,
  businessId,
  venueDetails,
  listNameCreated,
}: AddRemoveVenueToListProps) => {
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle } = useModalHeight();
  const showToast = useCustomToast();

  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceIdToViewDataChanged] = useAtom(
    savedPlaceIdToViewDataChangedAtom,
  );

  const { data, refetch } = useSavedPlaces();
  const { addToLists, error, isLoading, result } = usePostSaveVenue();
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState("");

  useEffect(() => {
    if (data && data?.saved_places) {
      // console.log("data", data);
      const newList = [];

      data.saved_places
        .filter((item) => !item.is_default_list)
        .forEach((item) => {
          // console.log("item", item);
          item?.saved_place_details.forEach((innerItem) => {
            if (innerItem.business_id === businessId) {
              newList.push(innerItem.saved_place_id);
            }
          });

          // console.log("listNameCreated", listNameCreated, (listNameCreated === item.name));
          if (listNameCreated === item.name) {
            newList.push(item.id);
          }
        });

      setSelectedList(newList);
    }
  }, [listNameCreated, businessId, data]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler(
        "mySavedPlacesErrorSubmittingAddToSavedPlaces",
      );
      AnalyticsService.trackMx("mySavedPlacesErrorSubmittingAddToSavedPlaces", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (result) {
      console.log("in result savedPlaceIdToView", savedPlaceIdToView);

      AnalyticsService.trackGAEventHandler("mySavedPlacesClickedSaveToList");
      AnalyticsService.trackMx("mySavedPlacesClickedSaveToList", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      // console.log("result", result);
      showToast(result.message, "success");
      refetch();

      if (savedPlaceIdToView) {
        // reload map
        console.log("reload map");
        setSavedPlaceIdToViewDataChanged(true);
      }

      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, i18n]);

  const handleSelectedList = (id: number, listName: string) => {
    if (selectedList.includes(id)) {
      AnalyticsService.trackGAEventHandler("mySavedPlacesVenueRemovedFromList");
      AnalyticsService.trackMx("mySavedPlacesVenueRemovedFromList", {});

      setSelectedList(selectedList.filter((item) => item !== id));
      setSelectionAnnouncement(`Removed from ${listName}`);
    } else {
      AnalyticsService.trackGAEventHandler("mySavedPlacesVenueAddedToList");
      AnalyticsService.trackMx("mySavedPlacesVenueAddedToList", {});

      setSelectedList([...selectedList, id]);
      setSelectionAnnouncement(`Added to ${listName}`);
    }
    
    // Clear announcement after screen reader has time to read it
    setTimeout(() => setSelectionAnnouncement(""), 1000);
  };

  // Function to handle image load failure
  const placeHolderImage = "/images/business_default_image.svg";
  const handleImageLoadError = (event) => {
    event.target.src = placeHolderImage;
    event.target.style = "object-fit:contain";
  };

  const closeButtonRef = useRef<HTMLButtonElement>(null); // Reference for initial focus

  // Focus on the close button when the component mounts
  useEffect(() => {
    setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 100);
  }, []);

  return (
    <section
      style={modalStyle}
      className="flex flex-col bg-bg-brand-secondary-default rounded-xl overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {isLoading && <Loader />}
      
      {/* Screen reader announcements for selections and errors */}
      <div role="status" aria-live="polite" className="sr-only">
        {selectionAnnouncement}
      </div>
      <div role="alert" aria-live="assertive" className="sr-only">
        {error && error.message}
      </div>

      <header
        className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          ref={closeButtonRef}
          onClick={onClose}
          variant="closeModal"
          icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} aria-hidden="true" />}
          aria-label="Close modal"
          tabIndex={0}
          className="flex-shrink-0"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          id="modal-title"
        >
          {i18n.get("addToSavedPlaces")}
        </h1>
        <div className="flex-shrink-0 w-10"></div>
        {" "}
        {/* Spacer to balance the layout */}
      </header>

      <div
        className="flex-1 overflow-y-auto py-3 px-3"
      >
        <div className="px-4 md:px-6 py-6 flex flex-col gap-6">
          {/* Default Lists - Always shown first with CheckCircle, no interaction */}
          {data?.saved_places
            .filter((item) => item.is_default_list)
            .map((item, index) => (
              <div key={`default-${index}`} className="flex items-center gap-6">
                <div>
                  {item.cover_photo ? (
                    <img
                      src={bucketUrlPrefix + item.cover_photo}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  ) : item.saved_place_details.length > 0 &&
                    item.saved_place_details[0].photo_thumb != "" &&
                    item.saved_place_details[0].photo_thumb != null ? (
                    <img
                      src={
                        bucketUrlPrefix +
                        item.saved_place_details[0].photo_thumb
                      }
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  ) : (
                    <img
                      src={placeHolderImage}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  )}
                </div>

                <div className="flex-grow">
                  <div className="title-s-semibold text-fg-neutral-strong">
                    {item.name}
                  </div>
                  <div className="body-l-semibold text-fg-brand-subtle">
                    {item.total_places.toString() + " place saved"}
                  </div>
                </div>
                <div>
                 <CheckCircle size={24} weight="fill" className="text-fg-brand-subtle" aria-hidden="true" />
                </div>
              </div>
            ))}

          {/* Separator after default lists */}
          {data?.saved_places.some((item) => item.is_default_list) && (
            <div className="w-full border-t border-border-token-brand-minimal"></div>
          )}

          <button
            type="button"
            className="flex items-center gap-6"
            onClick={onClickCreateNew}
            aria-label={i18n.get("createANewList")}
          >
            <div className="rounded-lg">
              <img
                src={
                  isDark
                    ? "/images/CreateSavedPlaceIcon_dark.svg"
                    : "/images/createListBox.svg"
                }
                alt=""
                className="w-[104px] h-[104px] rounded-lg"
                aria-hidden="true"
              />
            </div>
            <span className="title-s-semibold text-fg-neutral-strong">
              {i18n.get("createANewList")}
            </span>
          </button>

          {/* User-created lists - Interactive */}
          {data?.saved_places
            .filter((item) => !item.is_default_list)
            .sort((a: any, b: any) => {
              // Sort by created_at descending (newest first)
              return (
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
              );
            })
            .map((item, index) => (
              <div
                key={index}
                className="cursor-pointer flex items-center gap-6 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                onClick={() => {
                  handleSelectedList(item.id, item.name);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectedList(item.id, item.name);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={
                  selectedList.includes(item.id)
                    ? `${item.name} selected`
                    : `${item.name}`
                }
              >
                <div
                  className={
                    listNameCreated === item.name
                      ? "rounded-lg outline outline-2 outline-border-token-brand-subtle outline-offset-0"
                      : ""
                  }
                >
                  {item.cover_photo ? (
                    <img
                      src={bucketUrlPrefix + item.cover_photo}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  ) : item.saved_place_details.length > 0 &&
                    item.saved_place_details[0].photo_thumb != "" &&
                    item.saved_place_details[0].photo_thumb != null ? (
                    <img
                      src={
                        bucketUrlPrefix +
                        item.saved_place_details[0].photo_thumb
                      }
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  ) : (
                    <img
                      src={placeHolderImage}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[104px]"
                      onError={handleImageLoadError}
                    />
                  )}
                </div>

                <div className="flex-grow">
                  <div className="title-s-semibold text-fg-neutral-strong">
                    {item.name}
                  </div>

                  {listNameCreated !== item.name && (
                    <div className="body-l-semibold text-fg-brand-subtle">
                      {item.total_places.toString() + " place saved"}
                    </div>
                  )}

                  {listNameCreated === item.name && (
                    <div className="h-6 px-4 py-1 bg-container-brand-subtle rounded-[20px] justify-start items-start inline-flex text-fg-neutral-onFilled">
                      <div className="label-s-medium text-fg-neutral-onFilled">New List</div>
                    </div>
                  )}
                </div>
                <div>
                  {selectedList.includes(item.id) ? (
                    <CheckCircle size={24} weight="fill" className="text-fg-brand-subtle" aria-hidden="true" />
                  ) : (
                    <PlusCircle size={24} weight="regular" className="text-fg-brand-subtle" aria-hidden="true" />
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default sticky bottom-0 z-10">
        <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
          <div className="w-[327px] justify-center items-center inline-flex">
            <WideButton
              className="grow"
              onClick={() => {
                console.log("ssubmit clicked", businessId);

                if (!businessId) return;

                addToLists({
                  listIds: selectedList,
                  venueId: businessId,
                });
              }}
            >
              {i18n.get("save")}
            </WideButton>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default AddRemoveVenueToList;
